/**
 * Mass Construction — Detalle
 * Controles custom de vídeo (play/pause, mute, fullscreen, timer),
 * cambio de fuente por orientación/plataforma, y drawer OWOWH!!.
 */
(function () {
  'use strict';

  const BREAKPOINT_MOBILE = 749;

  // ────────────────────────────────────────────────────────────────────────────
  // Clase: VideoController
  // Gestiona el vídeo activo y sus controles
  // ────────────────────────────────────────────────────────────────────────────

  class VideoController {
    constructor(sectionEl) {
      this.section = sectionEl;

      this._videos = {
        desktop:        sectionEl.querySelector('[data-mc-video="desktop"]'),
        mobilePortrait: sectionEl.querySelector('[data-mc-video="mobile-portrait"]'),
        mobileLandscape: sectionEl.querySelector('[data-mc-video="mobile-landscape"]'),
      };

      this._poster      = sectionEl.querySelector('[data-mc-poster]');
      this._activeVideo = null;
      this._timerRaf    = null;

      this._mqMobile      = window.matchMedia(`(max-width: ${BREAKPOINT_MOBILE}px)`);
      this._mqLandscape   = window.matchMedia('(orientation: landscape)');

      this._initActiveVideo();
      this._bindControls();
      this._bindOrientationChange();
      this._startTimer();
    }

    // ── Selección del vídeo activo ───────────────────────────────────────────

    _resolveActiveVideo() {
      if (!this._mqMobile.matches) {
        return this._videos.desktop || this._videos.mobileLandscape || this._videos.mobilePortrait;
      }
      if (this._mqLandscape.matches) {
        return this._videos.mobileLandscape || this._videos.desktop || this._videos.mobilePortrait;
      }
      return this._videos.mobilePortrait || this._videos.desktop || this._videos.mobileLandscape;
    }

    _initActiveVideo() {
      this._activeVideo = this._resolveActiveVideo();
      if (!this._activeVideo) return;

      this._syncActiveVideoClass();
      this._playActiveVideo({ muted: true, unmuteAfterPlay: true });
      this._syncPlayButton();
      this._syncMuteButton();
    }

    _playActiveVideo({ muted = true, unmuteAfterPlay = false, allowMutedFallback = true } = {}) {
      const video = this._activeVideo;
      if (!video) return;

      this._setVideoMuted(video, muted);
      video.playsInline = true;
      this._stripNativePoster(video);

      const doPlay = () => {
        const playPromise = video.play();
        if (!playPromise?.catch) return;

        playPromise
          .then(() => {
            this._setVideoMuted(video, muted);
            if (unmuteAfterPlay) this._tryUnmuteAfterAutoplay(video);
            this._syncMuteButton();
          })
          .catch(() => {
            if (!allowMutedFallback || muted) return;

            this._setVideoMuted(video, true);
            this._syncMuteButton();

            video.play()
              .then(() => {
                if (unmuteAfterPlay) this._tryUnmuteAfterAutoplay(video);
                this._syncMuteButton();
              })
              .catch(() => { /* autoplay bloqueado */ });
          });
      };

      video.addEventListener('playing', () => this._hidePoster(), { once: true });
      video.addEventListener('timeupdate', () => {
        if (video.currentTime > 0) this._hidePoster();
      }, { once: true });

      if (video.readyState >= 2) {
        doPlay();
      } else {
        if (video.readyState === 0) video.load();
        video.addEventListener('canplay', doPlay, { once: true });
      }
    }

    _tryUnmuteAfterAutoplay(video) {
      if (!video) return;

      requestAnimationFrame(() => {
        this._setVideoMuted(video, false);
        this._syncMuteButton();

        window.setTimeout(() => {
          if (!video.paused) return;

          this._setVideoMuted(video, true);
          this._syncMuteButton();
          video.play().catch(() => { /* autoplay bloqueado */ });
        }, 150);
      });
    }

    _setVideoMuted(video, muted) {
      if (!video) return;
      video.muted = muted;
      video.defaultMuted = muted;
    }

    _stripNativePoster(video) {
      if (!video || !video.hasAttribute('poster')) return;
      video.removeAttribute('poster');
    }

    _hidePoster() {
      this._poster?.classList.add('is-hidden');
    }

    _syncActiveVideoClass() {
      Object.values(this._videos).forEach(v => v?.classList.remove('is-active'));
      this._activeVideo?.classList.add('is-active');
    }

    // ── Bind de controles ────────────────────────────────────────────────────

    _bindControls() {
      const q = (sel) => this.section.querySelector(sel);

      // Play / pause
      const playBtn = q('[data-mc-play]');
      playBtn?.addEventListener('click', () => this._togglePlay());

      // Mute
      const muteBtn = q('[data-mc-mute]');
      muteBtn?.addEventListener('click', () => this._toggleMute());

      // Fullscreen
      const fsBtn = q('[data-mc-fullscreen]');
      fsBtn?.addEventListener('click', () => this._toggleFullscreen());

      // Sincronizar botones cuando el vídeo cambia de estado externamente
      this._bindVideoEvents(this._activeVideo);

      // Fullscreen change (actualiza etiqueta del botón)
      document.addEventListener('fullscreenchange', () => this._syncFullscreenButton());
    }

    _bindVideoEvents(video) {
      if (!video) return;
      video.addEventListener('play',  () => this._syncPlayButton());
      video.addEventListener('pause', () => this._syncPlayButton());
      video.addEventListener('volumechange', () => this._syncMuteButton());
    }

    // ── Acciones ─────────────────────────────────────────────────────────────

    _togglePlay() {
      if (!this._activeVideo) return;
      if (this._activeVideo.paused) {
        this._activeVideo.play().catch(() => {});
      } else {
        this._activeVideo.pause();
      }
    }

    _toggleMute() {
      if (!this._activeVideo) return;
      this._activeVideo.muted = !this._activeVideo.muted;
      this._syncMuteButton();
    }

    _toggleFullscreen() {
      const target = this.section.querySelector('[data-mc-fullscreen-target]') || this.section;

      if (!document.fullscreenElement) {
        target.requestFullscreen?.() || target.webkitRequestFullscreen?.();
      } else {
        document.exitFullscreen?.() || document.webkitExitFullscreen?.();
      }
    }

    // ── Sincronización de UI ─────────────────────────────────────────────────

    _syncPlayButton() {
      const btn   = this.section.querySelector('[data-mc-play]');
      const label = this.section.querySelector('[data-mc-play-label]');
      if (!btn || !this._activeVideo) return;

      const isPaused = this._activeVideo.paused;
      btn.setAttribute('aria-label', isPaused ? 'Reproducir' : 'Pausar');
      if (label) label.textContent = isPaused ? 'PLAY' : 'PAUSE';
    }

    _syncMuteButton() {
      const btn   = this.section.querySelector('[data-mc-mute]');
      const label = this.section.querySelector('[data-mc-mute-label]');
      if (!btn || !this._activeVideo) return;

      const isMuted = this._activeVideo.muted;
      btn.setAttribute('aria-label', isMuted ? 'Activar sonido' : 'Silenciar');
      if (label) label.textContent = isMuted ? 'SOUND' : 'MUTE';
    }

    _syncFullscreenButton() {
      const btn   = this.section.querySelector('[data-mc-fullscreen]');
      const label = this.section.querySelector('[data-mc-fullscreen-label]');
      if (!btn) return;

      const isFs = !!document.fullscreenElement;
      btn.setAttribute('aria-label', isFs ? 'Salir de pantalla completa' : 'Pantalla completa');
      if (label) label.textContent = isFs ? 'EXIT FULL' : 'FULL';
    }

    // ── Timer ────────────────────────────────────────────────────────────────

    _startTimer() {
      const timeEl = this.section.querySelector('[data-mc-time]');
      if (!timeEl) return;

      const update = () => {
        const video = this._activeVideo;
        if (video) {
          const totalSecs = video.currentTime;
          const m  = Math.floor(totalSecs / 60);
          const s  = Math.floor(totalSecs % 60);
          const cs = Math.floor((totalSecs % 1) * 100); // centisegundos
          const formatted = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}:${String(cs).padStart(2, '0')}`;
          timeEl.textContent = formatted;
          timeEl.setAttribute('datetime', `PT${m}M${s}S`);
        }
        this._timerRaf = requestAnimationFrame(update);
      };

      this._timerRaf = requestAnimationFrame(update);
    }

    _stopTimer() {
      if (this._timerRaf) {
        cancelAnimationFrame(this._timerRaf);
        this._timerRaf = null;
      }
    }

    // ── Cambio de orientación / breakpoint ───────────────────────────────────

    _bindOrientationChange() {
      this._mqLandscape.addEventListener('change', () => this._handleSourceSwitch());
      this._mqMobile.addEventListener('change',    () => this._handleSourceSwitch());
    }

    _handleSourceSwitch() {
      const prev        = this._activeVideo;
      const wasPlaying  = prev && !prev.paused;
      const wasMuted    = prev ? prev.muted : false;

      // Pausar todos
      Object.values(this._videos).forEach(v => v?.pause());

      // Resolver nuevo vídeo activo
      this._activeVideo = this._resolveActiveVideo();
      if (!this._activeVideo) return;
      this._syncActiveVideoClass();

      // Heredar muted (el usuario puede haber activado sonido)
      this._setVideoMuted(this._activeVideo, wasMuted);

      if (wasPlaying) {
        this._playActiveVideo({ muted: wasMuted, unmuteAfterPlay: !wasMuted });
      }

      // Re-bind eventos al nuevo vídeo activo
      this._bindVideoEvents(this._activeVideo);
      this._syncPlayButton();
      this._syncMuteButton();
    }

    // ── Destrucción ──────────────────────────────────────────────────────────

    destroy() {
      this._stopTimer();
      Object.values(this._videos).forEach(v => v?.pause());
    }
  }


  // ────────────────────────────────────────────────────────────────────────────
  // Clase: DrawerController
  // Gestiona apertura/cierre del panel OWOWH!! con focus trap y a11y completa
  // ────────────────────────────────────────────────────────────────────────────

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), a[href], input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class DrawerController {
    constructor(sectionEl) {
      this.section  = sectionEl;
      this.drawer   = sectionEl.querySelector('[data-mc-drawer]');
      this.trigger  = sectionEl.querySelector('[data-mc-drawer-trigger]');
      this.panel    = this.drawer?.querySelector('.mc-drawer__panel');
      this.backdrop = sectionEl.querySelector('[data-mc-drawer-backdrop]');
      this.isOpen   = false;
      this._prevFocus   = null;
      this._keyHandler  = this._onKeyDown.bind(this);

      if (!this.drawer || !this.panel) return;

      // Todos los botones [data-mc-drawer-close] dentro del drawer
      this.drawer.querySelectorAll('[data-mc-drawer-close]')
        .forEach(btn => btn.addEventListener('click', () => this.close()));

      this.trigger?.addEventListener('click', () => this.open());

      // Cerrar al click en backdrop
      this.backdrop?.addEventListener('pointerdown', (e) => {
        if (e.target === this.backdrop) this.close();
      });
    }

    // ── Abrir ────────────────────────────────────────────────────────────────

    open() {
      if (this.isOpen) return;
      this.isOpen    = true;
      this._prevFocus = document.activeElement;

      // Compensar scrollbar para evitar salto de layout
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.documentElement.style.paddingRight = `${sbw}px`;

      // Bloquear scroll
      document.body.style.overflow = 'hidden';

      // Marcar contenido detrás como inerte (bloquea tab + screen readers)
      this._setBackgroundInert(true);

      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.trigger?.setAttribute('aria-expanded', 'true');

      // Enfocar el primer elemento focusable tras la animación (45ms de margen)
      this._focusFirst();

      document.addEventListener('keydown', this._keyHandler);
    }

    // ── Cerrar ───────────────────────────────────────────────────────────────

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');

      // Revertir bloqueo de scroll y scrollbar
      document.body.style.overflow = '';
      document.documentElement.style.paddingRight = '';

      // Reactivar contenido detrás
      this._setBackgroundInert(false);

      // Devolver foco al trigger (o elemento anterior)
      requestAnimationFrame(() => {
        (this._prevFocus || this.trigger)?.focus();
        this._prevFocus = null;
      });

      document.removeEventListener('keydown', this._keyHandler);
    }

    // ── Focus helpers ────────────────────────────────────────────────────────

    _focusFirst() {
      // Esperamos al siguiente frame para que el panel ya sea visible
      requestAnimationFrame(() => {
        const el = this.panel.querySelector(FOCUSABLE_SELECTOR);
        el?.focus({ preventScroll: true });
      });
    }

    _getFocusable() {
      return [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        el => !el.closest('[aria-hidden="true"]')
      );
    }

    // ── Focus trap ───────────────────────────────────────────────────────────

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = this._getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    // ── Inert helper ─────────────────────────────────────────────────────────

    _setBackgroundInert(inert) {
      // Marcar como inert todos los hermanos directos del drawer a nivel de sección
      // (excluye el drawer mismo para no bloquearlo)
      const siblings = [...this.section.children].filter(el => el !== this.drawer);
      siblings.forEach(el => {
        if (inert) {
          el.setAttribute('inert', '');
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.removeAttribute('inert');
          el.removeAttribute('aria-hidden');
        }
      });
    }

    // ── Destrucción ──────────────────────────────────────────────────────────

    destroy() {
      document.removeEventListener('keydown', this._keyHandler);
      document.body.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      this._setBackgroundInert(false);
    }
  }


  // ────────────────────────────────────────────────────────────────────────────
  // Bootstrap
  // ────────────────────────────────────────────────────────────────────────────

  function initSection(sectionEl) {
    if (sectionEl._mcDetail) {
      sectionEl._mcDetail.video?.destroy();
      sectionEl._mcDetail.drawer?.destroy();
    }

    sectionEl._mcDetail = {
      video:  new VideoController(sectionEl),
      drawer: new DrawerController(sectionEl),
    };
  }

  function destroySection(sectionEl) {
    if (!sectionEl._mcDetail) return;
    sectionEl._mcDetail.video?.destroy();
    sectionEl._mcDetail.drawer?.destroy();
    delete sectionEl._mcDetail;
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-mc-detail]').forEach(initSection);
  });

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-mc-detail]');
    if (el) initSection(el);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('[data-mc-detail]');
    if (el) destroySection(el);
  });

})();
